import { motion } from "framer-motion";
import AIim from "../assests/AIim.mp4";

export default function ImageVideoCollection() {
  return (
    <div className="bg-gray-50 text-gray-900">
      {/* Hero Section with Video Background */}
      <section className="relative w-full h-screen h-[70vh] text-white flex items-center justify-center text-center">
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source src={AIim} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
        <div className="absolute inset-0 bg-black bg-opacity-60"></div>
        <div className="relative z-10 px-6">
          <motion.h1
            initial={{ opacity: 0, y: -40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-4xl md:text-6xl font-bold mb-4 tracking-tight font-orbitron"
          >
            Building Smarter AI with Image & Video Collection
          </motion.h1>
          <p className="text-lg md:text-xl max-w-2xl mx-auto leading-relaxed font-raleway">
            High-quality, diverse, and well-structured datasets to power your
            computer vision, AR/VR, and AI models.
          </p>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 px-6 text-center max-w-4xl mx-auto">
        <h2 className="text-4xl font-semibold mb-6 tracking-tight font-orbitron">
          Why Image & Video Data Matters
        </h2>
        <p className="text-lg text-gray-700 leading-relaxed font-raleway">
          We collect, curate, and annotate images and videos that help AI systems
          learn to see the world more accurately. From everyday objects to
          complex human interactions, our datasets empower:
        </p>
        <ul className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4 text-left text-base text-gray-700 leading-relaxed font-raleway">
          <li>✔ Computer Vision Models</li>
          <li>✔ Facial Recognition Systems</li>
          <li>✔ Autonomous Vehicles</li>
          <li>✔ Healthcare AI</li>
          <li>✔ AR/VR Experiences</li>
        </ul>
      </section>

      {/* Collections Section */}
      <section className="py-20 px-6 bg-gray-100">
        <h2 className="text-4xl font-semibold text-center mb-12 tracking-tight font-orbitron">
          Our Collections
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto font-raleway">
          {[
            {
              title: "📸 Image Collection",
              desc: "Over 1M+ images across industries, multi-angle, multi-lighting scenarios, and inclusive datasets.",
            },
            {
              title: "🎥 Video Collection",
              desc: "High-resolution videos in real-world scenarios for human activity recognition, traffic, retail, and medical datasets.",
            },
            {
              title: "📝 Annotation Services",
              desc: "Bounding boxes, segmentation, labeling, and domain-specific expertise for advanced AI.",
            },
          ].map((item, idx) => (
            <div
              key={idx}
              className="bg-white p-6 rounded-2xl shadow hover:shadow-xl transition-shadow duration-300"
            >
              <h3 className="text-xl font-semibold mb-3 font-orbitron">{item.title}</h3>
              <p className="text-gray-700 leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 px-6">
        <h2 className="text-4xl font-semibold text-center mb-12 tracking-tight font-orbitron">
          How We Collect & Deliver
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 max-w-6xl mx-auto text-center font-raleway">
          {[
            "Define project requirements",
            "Source or capture data",
            "Annotate with experts",
            "Quality assurance & delivery",
          ].map((step, idx) => (
            <div key={idx} className="p-4">
              <div className="text-4xl mb-2">{`${idx + 1}️⃣`}</div>
              <p className="text-gray-700 leading-relaxed">{step}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-20 px-6 bg-gray-100">
        <h2 className="text-4xl font-semibold text-center mb-12 tracking-tight font-orbitron">
          Applications of Our Data
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto font-raleway">
          {[
            { title: "🚗 Autonomous Driving", desc: "Road & pedestrian detection for self-driving cars." },
            { title: "🛒 Retail AI", desc: "Customer behavior recognition and smart retail analytics." },
            { title: "🏥 Medical AI", desc: "Disease detection & diagnosis support using annotated datasets." },
            { title: "🔒 Security", desc: "Face & gesture recognition for authentication and monitoring." },
          ].map((item, idx) => (
            <div
              key={idx}
              className="bg-white p-6 rounded-2xl shadow hover:shadow-xl transition-shadow duration-300"
            >
              <h3 className="font-semibold mb-2 text-lg font-orbitron">{item.title}</h3>
              <p className="text-gray-700 leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="py-24 text-center bg-gradient-to-r from-indigo-600 to-purple-700 text-white">
        <h2 className="text-4xl md:text-5xl font-bold mb-6 tracking-tight font-orbitron">
          Ready to Power Your AI With the Right Data?
        </h2>
        <p className="text-lg max-w-xl mx-auto font-raleway">
          Let’s build smarter systems together. Reach out to get started.
        </p>
      </section>
    </div>
  );
}
